import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import Header from './Header';
import './InvoiceVerificationPage.css';

const InvoiceVerificationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, isAuthenticated, user, isLoading, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null);
  const [requestPdfUrl, setRequestPdfUrl] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);

  // Demo PDF mapping - Fallback if PDF not found in database
  const DEMO_PDF_MAP: { [key: string]: string } = {
    '690a51fdf30546493e7b36b2': '/demo_istek/nalbur.pdf',
    '690a51f8f30546493e7b36b1': '/demo_istek/platin.pdf'
  };

  // RFQ ID to fetch
  const RFQ_ID = '68d2508d225b9401400ba5a6';

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated && token && id) {
      // Show initial loading for 3 seconds
      setInitialLoading(true);
      const initialTimer = setTimeout(() => {
        setInitialLoading(false);
      }, 3000);

      // Start fetching PDFs (RFQ data is now manual, no need to fetch)
      fetchInvoicePDF();
      loadRequestPDF();

      return () => clearTimeout(initialTimer);
    }
  }, [isAuthenticated, token, id]);

  // RFQ data is now manual, no need to fetch
  // const fetchRFQData = async () => { ... }

  const fetchInvoicePDF = async () => {
    try {
      const response = await fetch(
        API_ENDPOINTS.INVOICE_DOWNLOAD(id!),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setInvoicePdfUrl(url);
    } catch (err) {
      console.error('Error fetching invoice PDF:', err);
      setError('Failed to load invoice PDF');
    }
  };

  const loadRequestPDF = async () => {
    try {
      // Fetch PDF from backend using invoice ID
      const response = await fetch(
        API_ENDPOINTS.DEMO_ISTEK_BY_INVOICE(id!),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // If PDF not found, try fallback to demo mapping
        const pdfPath = DEMO_PDF_MAP[id!];
        if (pdfPath) {
          console.warn(`PDF not found in database for invoice ID: ${id}, using fallback`);
          setRequestPdfUrl(pdfPath);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setRequestPdfUrl(url);
    } catch (err) {
      console.error('Error fetching request PDF:', err);
      // Try fallback to demo mapping
      const pdfPath = DEMO_PDF_MAP[id!];
      if (pdfPath) {
        console.warn(`Using fallback PDF path for invoice ID: ${id}`);
        setRequestPdfUrl(pdfPath);
      } else {
        setError('Failed to load request PDF');
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const handleErrorAnalysis = () => {
    // Only show analysis for specific invoice IDs
    if (id !== '690a51fdf30546493e7b36b2' && id !== '690a51f8f30546493e7b36b1') {
      return;
    }

    setAnalyzing(true);
    
    // Show analyzing for 3 seconds
    setTimeout(() => {
      setAnalyzing(false);
      setAnalysisComplete(true);
    }, 3000);
  };

  // Get products data based on invoice ID
  const getRequestProducts = () => {
    if (id === '690a51fdf30546493e7b36b2') {
      return [
        { code: 'SİST000010', name: 'Sista Silikon Beyaz 280 Ml', unit: 'Adet', quantity: 20, price: 165.00, total: 3300.00 },
        { code: 'NEYZ101583', name: 'Teflon Bant', unit: 'AD', quantity: 100, price: 4.61, total: 461.00 },
        { code: 'NEYZ134388', name: 'Storm Maskeleme Bandı 48mm X 20mt', unit: 'AD', quantity: 100, price: 21.00, total: 2100.00 },
        { code: 'SİST000007', name: 'Sista Silikon Şeffaf 280 Ml', unit: 'Adet', quantity: 40, price: 165.00, total: 6600.00 }
      ];
    } else if (id === '690a51f8f30546493e7b36b1') {
      return [
        { code: '1', name: 'SISTA Şeffaf Silikon 310 ML', unit: 'Adet', quantity: 20, price: 129.80, total: 2596.00 },
        { code: '2', name: 'SISTA Beyaz Silikon 310 ML', unit: 'Adet', quantity: 100, price: 7.70, total: 770.00 },
        { code: '3', name: 'Teflon Bant', unit: 'Adet', quantity: 100, price: 22.00, total: 2200.00 },
        { code: '4', name: 'Kağıt Bant 50MM', unit: 'Adet', quantity: 40, price: 129.80, total: 5192.00 },
        { code: '5', name: 'Kargo Bedeli', unit: 'Adet', quantity: 1, price: 450.00, total: 450.00 }
      ];
    }
    return [];
  };

  const getInvoiceProducts = () => {
    if (id === '690a51fdf30546493e7b36b2') {
      return [
        { code: 'SİST000010', name: 'Sista Silikon Beyaz 280 Ml', unit: 'Adet', quantity: 20, price: 165.00, total: 3300.00 },
        { code: 'NEYZ101583', name: 'Teflon Bant', unit: 'Adet', quantity: 100, price: 4.81, total: 481.00 },
        { code: 'NEYZ134388', name: 'Storm Maskeleme Bandı 48mm X 20mt', unit: 'Adet', quantity: 100, price: 26.00, total: 2600.00 },
        { code: 'SİST000007', name: 'Sista Silikon Şeffaf 280 Ml', unit: 'Adet', quantity: 40, price: 175.00, total: 7000.00 }
      ];
    } else if (id === '690a51f8f30546493e7b36b1') {
      return [
        { code: '1', name: 'SISTA Şeffaf Silikon 310 ML', unit: 'Adet', quantity: 20, price: 129.80, total: 2596.00 },
        { code: '2', name: 'SISTA Beyaz Silikon 310 ML', unit: 'Adet', quantity: 100, price: 7.70, total: 770.00 },
        { code: '3', name: 'Teflon Bant', unit: 'Adet', quantity: 100, price: 22.00, total: 2200.00 },
        { code: '4', name: 'Kağıt Bant 50MM', unit: 'Adet', quantity: 40, price: 129.80, total: 5192.00 },
        { code: '5', name: 'Kargo Bedeli', unit: 'Adet', quantity: 1, price: 450.00, total: 450.00 }
      ];
    }
    return [];
  };

  const getSummaryData = () => {
    if (id === '690a51fdf30546493e7b36b2') {
      return {
        taxBase: { request: 10384.17, invoice: 13381.00 },
        tax: { request: 2076.83, invoice: 2676.20 },
        total: { request: 12461.00, invoice: 16057.20 },
        hasError: true
      };
    } else if (id === '690a51f8f30546493e7b36b1') {
      return {
        taxBase: { request: 11208.00, invoice: 11208.00 },
        tax: { request: 2241.60, invoice: 2241.60 },
        total: { request: 13449.60, invoice: 13449.60 },
        hasError: false
      };
    }
    return { taxBase: { request: 0, invoice: 0 }, tax: { request: 0, invoice: 0 }, total: { request: 0, invoice: 0 }, hasError: false };
  };

  const requestProducts = getRequestProducts();
  const invoiceProducts = getInvoiceProducts();
  const summaryData = getSummaryData();

  // Check if price is different (has error)
  const hasPriceError = (requestProduct: any, invoiceProduct: any) => {
    return requestProduct.price !== invoiceProduct.price || requestProduct.total !== invoiceProduct.total;
  };

  // Show initial loading spinner for 3 seconds
  if (initialLoading) {
    return (
      <div className="App">
        <Header 
          onLogout={handleLogout} 
          userType={user?.role === 'admin' || user?.is_admin ? 'admin' : 'user'}
        />
        <div className="invoice-verification-page">
          <div className="initial-loading-container">
            <p className="loading-message">İlgili istek aranıyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || loading) {
    return (
      <div className="App">
        <Header 
          onLogout={handleLogout} 
          userType={user?.role === 'admin' || user?.is_admin ? 'admin' : 'user'}
        />
        <div className="invoice-verification-page">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (error) {
    return (
      <div className="App">
        <Header 
          onLogout={handleLogout} 
          userType={user?.role === 'admin' || user?.is_admin ? 'admin' : 'user'}
        />
        <div className="invoice-verification-page">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header 
        onLogout={handleLogout} 
        userType={user?.role === 'admin' || user?.is_admin ? 'admin' : 'user'}
      />
      <div className="invoice-verification-page">
        <div className="invoice-verification-header">
          <button 
            onClick={() => navigate('/invoice')}
            className="back-button"
          >
            ← Fatura Listesine Dön
          </button>
          <h1>Fatura Doğrulama</h1>
        </div>

        {/* RFQ Details Section */}
        <div className="rfq-details-section">
          <h2>RFQ Detayları</h2>
          <div className="rfq-details-grid">
            <div className="rfq-detail-card">
              <label>İstek Adı</label>
              <p>Hırdavat ve Nalbur</p>
            </div>
            <div className="rfq-detail-card">
              <label>İstek Tipi</label>
              <p>Çoklu</p>
            </div>
            <div className="rfq-detail-card">
              <label>Bütçe</label>
              <p>15.000 TL</p>
            </div>
            <div className="rfq-detail-card">
              <label>Kategori</label>
              <p>Ofis Malzemeleri</p>
            </div>
            <div className="rfq-detail-card">
              <label>Oluşturulma Tarihi</label>
              <p>17 Eylül 2025</p>
            </div>
            <div className="rfq-detail-card">
              <label>Durum</label>
              <p>Final</p>
            </div>
            <div className="rfq-detail-card">
              <label>İstek ID</label>
              <p>12</p>
            </div>
          </div>
        </div>

        {/* PDF Preview Section */}
        <div className="pdf-preview-section">
          <div className="pdf-preview-container">
            <div className="pdf-preview-item">
              <h3>Fatura PDF</h3>
              {invoicePdfUrl ? (
                <iframe
                  src={invoicePdfUrl}
                  className="pdf-preview-iframe"
                  title="Invoice PDF Preview"
                />
              ) : (
                <div className="pdf-loading">Fatura PDF yükleniyor...</div>
              )}
            </div>
            <div className="pdf-preview-item">
              <h3>İstek PDF</h3>
              {requestPdfUrl ? (
                <iframe
                  src={requestPdfUrl}
                  className="pdf-preview-iframe"
                  title="Request PDF Preview"
                />
              ) : (
                <div className="pdf-loading">İstek PDF yükleniyor...</div>
              )}
            </div>
          </div>
        </div>

        {/* Error Analysis Section */}
        {(id === '690a51fdf30546493e7b36b2' || id === '690a51f8f30546493e7b36b1') && (
          <div className="error-analysis-section">
            <button
              onClick={handleErrorAnalysis}
              className="error-analysis-button"
              disabled={analyzing || analysisComplete}
            >
              Hata Analizi
            </button>

            {analyzing && (
              <div className="analyzing-container">
                <p className="analyzing-message">Analiz ediliyor...</p>
              </div>
            )}

            {analysisComplete && (
              <div className="analysis-results">
                <h3 className={`analysis-title ${summaryData.hasError ? 'error-title' : 'success-title'}`}>
                  {summaryData.hasError ? (
                    'Aşağıda birkaç hata bulundu'
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }}>
                        <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Hata tespit edilmedi
                    </>
                  )}
                </h3>
                <div className="comparison-table-container">
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th className="product-code-col">Ürün Kodu</th>
                        <th className="product-name-col">Ürün Adı</th>
                        <th className="unit-col">Birim</th>
                        <th className="quantity-col">Adet</th>
                        <th className="price-col">
                          <div className="comparison-header">
                            <span className="request-label">İstek</span>
                            <span className="invoice-label">Fatura</span>
                          </div>
                          Birim Fiyat
                        </th>
                        <th className="total-col">
                          <div className="comparison-header">
                            <span className="request-label">İstek</span>
                            <span className="invoice-label">Fatura</span>
                          </div>
                          Toplam Fiyat
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {requestProducts.map((reqProduct, index) => {
                        const invProduct = invoiceProducts[index];
                        const hasError = hasPriceError(reqProduct, invProduct);
                        
                        return (
                          <tr key={reqProduct.code} className={hasError ? 'error-row' : ''}>
                            <td className="product-code-col">{reqProduct.code}</td>
                            <td className="product-name-col">{reqProduct.name}</td>
                            <td className="unit-col">{reqProduct.unit}</td>
                            <td className="quantity-col">{reqProduct.quantity}</td>
                            <td className="price-col">
                              <div className="price-comparison">
                                <span className="request-price">{reqProduct.price.toFixed(2)} TL</span>
                                <span className={`invoice-price ${hasError ? 'error-price' : ''}`}>
                                  {invProduct.price.toFixed(2)} TL
                                </span>
                              </div>
                            </td>
                            <td className="total-col">
                              <div className="price-comparison">
                                <span className="request-price">{reqProduct.total.toFixed(2)} TL</span>
                                <span className={`invoice-price ${hasError ? 'error-price' : ''}`}>
                                  {invProduct.total.toFixed(2)} TL
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} className="summary-label">Ara Toplam:</td>
                        <td colSpan={2} className="summary-value">
                          <div className="price-comparison">
                            <span className="request-price">{summaryData.taxBase.request.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL</span>
                            <span className={`invoice-price ${summaryData.hasError ? 'error-price' : ''}`}>
                              {summaryData.taxBase.invoice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </span>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={4} className="summary-label">KDV (%20):</td>
                        <td colSpan={2} className="summary-value">
                          <div className="price-comparison">
                            <span className="request-price">{summaryData.tax.request.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL</span>
                            <span className={`invoice-price ${summaryData.hasError ? 'error-price' : ''}`}>
                              {summaryData.tax.invoice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </span>
                          </div>
                        </td>
                      </tr>
                      <tr className="total-row">
                        <td colSpan={4} className="summary-label">Genel Toplam:</td>
                        <td colSpan={2} className="summary-value">
                          <div className="price-comparison">
                            <span className="request-price">{summaryData.total.request.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL</span>
                            <span className={`invoice-price ${summaryData.hasError ? 'error-price' : ''}`}>
                              {summaryData.total.invoice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </span>
                          </div>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceVerificationPage;

